import { AuthDeviceDB } from '@/accessors/indexedDB/authDeviceDB'
import MiiaFwConfig from '@/configs/config'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { getUserIdToStorage } from '@/modules/miiaFw/MiiaFwCognito'
import {
  EMPTY_BUILDIND,
  type Building
} from '@/types/accessor/api/emergencyInspectionResult/building/building'
import type { EmergencyAssessment } from '@/types/accessor/api/emergencyInspectionResult/emergencyAssessment/emergencyAssessment'
import type {
  ResGetItemInspectionResult,
  ResQueryInspectionResult
} from '@/types/accessor/api/emergencyInspectionResult/emergencyInspectionResult'
import { formatDate } from '@/utils/dateUtil/dateUtils'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { cloneDeep } from 'lodash'
import { defineStore } from 'pinia'
import { useNetworkStore } from './networkStore'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'

export type QueryStateType = Pick<ResGetItemInspectionResult, 'building'> &
  Pick<ResGetItemInspectionResult, 'assessmentId'> &
  Pick<ResGetItemInspectionResult, 'insertId'> &
  Pick<ResGetItemInspectionResult, 'updateDate'> &
  Pick<Building, 'location'> &
  Pick<Building, 'shapeCoordinates'> &
  Pick<EmergencyAssessment, 'overallResult'>

interface StoreState {
  queryState: QueryStateType[]
}

const initialState: StoreState = {
  queryState: [
    {
      building: EMPTY_BUILDIND,
      assessmentId: '',
      insertId: '',
      updateDate: '',
      location: undefined,
      shapeCoordinates: undefined,
      overallResult: null
    }
  ]
}

/** 調査実施結果API()用store */
export const useInspectionResultQueryStore = defineStore('inspectionResultQuery', {
  state: (): StoreState => cloneDeep(initialState),
  getters: { getStateAll: (state): StoreState => state },
  actions: {
    setQueryState(queryState: QueryStateType[]) {
      this.queryState = queryState
    },
    async fetchQueryState(disasterId: string | undefined, govCode: string | undefined) {
      // オフライン用に調査実施結果を取得（災害IDが必要になるため、災害情報の取得後に実行）
      const resQueryInspectionResult = await this.getInspectionResult(disasterId, govCode)

      // 調査実施結果API(query)用storeに必要事項を保存
      const inspectionResultQueryStoreState = resQueryInspectionResult?.resultList.map(item => {
        return {
          building: item.building,
          assessmentId: item.assessmentId,
          insertId: item.insertId,
          updateDate: item.updateDate,
          location: item.building.location,
          shapeCoordinates: item.building.shapeCoordinates,
          overallResult: item.emergencyAssessment.overallResult
        }
      })
      if (inspectionResultQueryStoreState != null) {
        this.setQueryState(inspectionResultQueryStoreState)
      }
    },
    async getInspectionResult(
      disasterId: string | undefined,
      govCode: string | undefined
    ): Promise<ResQueryInspectionResult | undefined> {
      if (disasterId == undefined) {
        throw new GlobalError([ERROR_CODES.HOME_DISASTER_ID_NOT_EXIST])
      }
      if (govCode == undefined) {
        throw new GlobalError([ERROR_CODES.HOME_GOV_CODE_NOT_EXIST])
      }

      const authDeviceDB = AuthDeviceDB.instance
      const deviceToken = await authDeviceDB.getDeviceToken()
      if (deviceToken == undefined) return
      const userId = getUserIdToStorage()
      if (userId == null) return
      const params = {
        system: MiiaFwConfig.config.sysId,
        govCode: govCode,
        deviceToken: deviceToken,
        disasterId: disasterId,
        insertId: userId,
        targetDate: formatDate(new Date(), 'YYYY-MM-DD')
      }
      const networkStore = useNetworkStore()
      const isOnline = networkStore.getIsOnline

      return await accessDataBasedOnNetwork('queryInspectionResultApi', params, isOnline, () => {
        networkStore.setOfflineStatus()
      }).catch(err => {
        console.error(err)
        throw new GlobalError([ERROR_CODES.HOME_INSPECTION_RESULT_GET_FAILED])
      })
    }
  }
})
